import React, { useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle2, ShoppingBag, Sparkles, Clock, Beaker, TrendingDown, ArrowRight, Activity, Scale, Zap, Sparkles as SparklesIcon, Hourglass, Moon, BoxIcon } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BUNDLES, Bundle } from '../components/bundles';
import { PRODUCTS } from '../components/products';
import { Vial } from '../components/Vial';
import { useCart } from '../components/cart/CartContext';
const GOAL_META: Record<Bundle['primaryGoal'], {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  label: string;
  short: string;
}> = {
  Recovery: {
    icon: Activity,
    label: 'For recovery research',
    short: 'Recovery'
  },
  'Weight management': {
    icon: Scale,
    label: 'For weight management research',
    short: 'Weight mgmt'
  },
  Performance: {
    icon: Zap,
    label: 'For performance research',
    short: 'Performance'
  },
  'Skin & Hair': {
    icon: SparklesIcon,
    label: 'For dermal research',
    short: 'Skin & Hair'
  },
  Longevity: {
    icon: Hourglass,
    label: 'For longevity research',
    short: 'Longevity'
  },
  Sleep: {
    icon: Moon,
    label: 'For sleep research',
    short: 'Sleep'
  }
};
function BundleCard({
  bundle
}: {
  bundle: Bundle;
}) {
  const {
    addItem
  } = useCart();
  const [added, setAdded] = useState(false);
  const items = bundle.productIds.map(id => PRODUCTS.find(p => p.id === id)).filter(Boolean) as typeof PRODUCTS;
  const gross = items.reduce((s, p) => s + p.price, 0);
  const net = gross * (1 - bundle.discount);
  const savings = gross - net;
  const handleAdd = () => {
    items.forEach(p => addItem(p.id, 1));
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };
  const goalMeta = GOAL_META[bundle.primaryGoal];
  const GoalIcon = goalMeta.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 14
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    duration: 0.4
  }} className="relative rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1061">
      {/* Single-row top bar: goal + badges + savings — all in one band */}
      <div className="flex items-center flex-wrap gap-3 px-5 lg:px-6 py-3 border-b border-border bg-primary/[0.04]" data-id="element-1062">
        <div className="inline-flex items-center gap-2" data-id="element-1063">
          <GoalIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" strokeWidth={2} data-id="element-1064" />
          <span className="text-[10.5px] uppercase tracking-[0.08em] font-bold text-primary" data-id="element-1065">
            {goalMeta.label}
          </span>
        </div>
        {bundle.badge && <span className="text-[10px] uppercase tracking-[0.06em] font-bold px-2 py-0.5 rounded-full bg-foreground text-background leading-none" data-id="element-1066">
            {bundle.badge}
          </span>}
        <span className="inline-flex items-center gap-1 font-mono text-[10.5px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-1067">
          <Clock className="h-3 w-3" strokeWidth={1.75} data-id="element-1068" />
          {bundle.protocolLength}
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary text-primary-foreground" data-id="element-1069">
          <TrendingDown className="h-3 w-3" strokeWidth={2.5} data-id="element-1070" />
          <span className="font-mono text-[11px] font-bold leading-none [font-feature-settings:'tnum']" data-id="element-1071">
            −{Math.round(bundle.discount * 100)}% · save £{savings.toFixed(0)}
          </span>
        </span>
      </div>

      {/* Main row — vials thumbnail strip + title + price + CTA */}
      <div className="flex flex-col lg:flex-row items-stretch" data-id="element-1072">
        {/* Vials thumbnail */}
        <div className="lg:w-[180px] flex-shrink-0 flex items-end justify-center gap-1 px-5 pt-6 pb-2 lg:py-5 bg-secondary/30 border-b lg:border-b-0 lg:border-r border-border text-foreground" data-id="element-1073">
          {items.map((p, i) => <div key={p.id} style={{
          transform: `translateY(${i % 2 === 0 ? -2 : 2}px)`,
          zIndex: items.length - i
        }} data-id="element-1074">
              <Vial label={p.name} doseMg={p.doseMg} volumeMl={p.volumeMl} category={p.category} size={items.length >= 3 ? 90 : 110} showHighlight showShadow={false} data-id="element-1075" />
            </div>)}
        </div>

        {/* Content */}
        <div className="flex-1 p-5 lg:p-6 flex flex-col gap-4" data-id="element-1076">
          {/* Title + price (in same row) */}
          <div className="flex items-start justify-between gap-5 flex-wrap" data-id="element-1077">
            <div className="min-w-0 flex-1" data-id="element-1078">
              <h3 className="text-[22px] lg:text-[26px] font-semibold tracking-[-0.02em] text-foreground leading-[1.15]" data-id="element-1079">
                {bundle.name}
              </h3>
              <p className="mt-1 text-[13px] text-muted-foreground" data-id="element-1080">
                {bundle.tagline}
              </p>
            </div>
            <div className="text-right flex-shrink-0" data-id="element-1081">
              <div className="flex items-baseline gap-2 justify-end" data-id="element-1082">
                <span className="font-mono text-[12px] text-muted-foreground line-through [font-feature-settings:'tnum']" data-id="element-1083">
                  £{gross.toFixed(0)}
                </span>
                <span className="font-mono text-[30px] font-medium text-foreground tracking-[-0.02em] leading-none [font-feature-settings:'tnum']" data-id="element-1084">
                  £{net.toFixed(0)}
                </span>
              </div>
              <div className="mt-1 font-mono text-[10.5px] font-bold text-primary [font-feature-settings:'tnum']" data-id="element-1085">
                Save £{savings.toFixed(0)}
              </div>
            </div>
          </div>

          {/* Why + What ships — side by side */}
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-3 lg:gap-4" data-id="element-1086">
            {/* Why this stack */}
            <ul className="space-y-1.5" data-id="element-1087">
              {bundle.whyThisStack.map((reason, i) => <li key={i} className="flex items-start gap-2" data-id="element-1088">
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-1089" />
                  <span className="text-[12.5px] text-foreground/85 leading-snug" data-id="element-1090">
                    {reason}
                  </span>
                </li>)}
            </ul>

            {/* Products list */}
            <ul className="space-y-1 lg:border-l lg:border-border lg:pl-4" data-id="element-1091">
              <li className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium pb-1" data-id="element-1092">
                {items.length} vials · 1 cold-chain box
              </li>
              {items.map(p => <li key={p.id} data-id="element-1093">
                  <Link to={`/shop/${p.slug}`} className="flex items-center gap-2 py-1 hover:text-foreground transition-colors" data-id="element-1094">
                    <span className="text-[12.5px] font-semibold tracking-tight text-foreground truncate" data-id="element-1095">
                      {p.name}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-1096">
                      <span className="text-foreground" data-id="element-1097">{p.doseMg}</span>
                      <span className="text-primary font-semibold" data-id="element-1098">mg</span>
                    </span>
                    <span className="ml-auto font-mono text-[11.5px] font-medium text-muted-foreground [font-feature-settings:'tnum']" data-id="element-1099">
                      £{p.price}
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* CTA row */}
          <div className="flex items-center justify-end pt-1" data-id="element-1100">
            <button onClick={handleAdd} className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-primary text-primary-foreground text-[13.5px] font-semibold tracking-tight transition-all [box-shadow:0_1px_0_0_rgba(255,255,255,0.18)_inset,0_1px_2px_rgba(20,60,30,0.15),0_4px_10px_-3px_rgba(20,60,30,0.32)] hover:-translate-y-px hover:[box-shadow:0_1px_0_0_rgba(255,255,255,0.22)_inset,0_2px_4px_rgba(20,60,30,0.18),0_10px_22px_-6px_rgba(20,60,30,0.45)] active:translate-y-0" data-id="element-1101">
              {added ? <>
                  <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} data-id="element-1102" />
                  Added to cart
                </> : <>
                  <ShoppingBag className="h-4 w-4" strokeWidth={2} data-id="element-1103" />
                  Add stack · £{net.toFixed(0)}
                </>}
            </button>
          </div>
        </div>
      </div>
    </motion.article>;
}
const FILTER_GOALS = ['All', 'Recovery', 'Weight management', 'Performance', 'Longevity'] as const;
type FilterGoal = (typeof FILTER_GOALS)[number];
export function Bundles() {
  const [activeGoal, setActiveGoal] = useState<FilterGoal>('All');
  const filtered = useMemo(() => {
    if (activeGoal === 'All') return BUNDLES;
    return BUNDLES.filter(b => b.primaryGoal === activeGoal);
  }, [activeGoal]);
  return <div className="min-h-screen w-full bg-background" data-id="element-1104">
      <AnnouncementBar data-id="element-1105" />
      <Header data-id="element-1106" />

      <main data-id="element-1107">
        {/* Header */}
        <section className="w-full border-b border-border" data-id="element-1108">
          <div className="container mx-auto pt-10 lg:pt-14 pb-10" data-id="element-1109">
            <nav className="flex items-center gap-1.5 text-[12px] text-muted-foreground mb-6" data-id="element-1110">
              <Link to="/" className="hover:text-foreground transition-colors" data-id="element-1111">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1112" />
              <span className="text-foreground" data-id="element-1113">Bundles</span>
            </nav>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45
          }} className="max-w-3xl" data-id="element-1114">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3 inline-flex items-center gap-2" data-id="element-1115">
                <Sparkles className="h-3 w-3 text-primary" strokeWidth={2} data-id="element-1116" />
                Curated research stacks
              </p>
              <h1 className="text-4xl lg:text-6xl font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-1117">
                Save up to <span className="text-primary" data-id="element-1118">15%</span> on the
                <br data-id="element-1119" />
                peptide stacks researchers
                <br data-id="element-1120" />
                actually pair<span className="font-mono text-primary" data-id="element-1121">.</span>
              </h1>
              <p className="mt-5 text-[16px] text-muted-foreground max-w-xl leading-relaxed" data-id="element-1122">
                Pre-paired by our lab team from the literature. One cold-chain
                box, matched batch documentation, a built-in discount that grows
                with the stack size.
              </p>
            </motion.div>
          </div>
        </section>

        {/* VALUE PROP STRIP — why buy a stack */}
        <section className="w-full border-b border-border bg-secondary/30" data-id="element-1123">
          <div className="container mx-auto py-8 lg:py-10" data-id="element-1124">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10" data-id="element-1125">
              {[{
              icon: Beaker,
              title: 'Literature-paired',
              body: 'Every stack mirrors a peer-reviewed in-vitro pairing — not marketing combinations.'
            }, {
              icon: TrendingDown,
              title: '10–15% off',
              body: 'Built-in discount that grows with the stack size. Single click vs sourcing each separately.'
            }, {
              icon: BoxIcon,
              title: 'One cold-chain box',
              body: 'All vials ship in a single insulated dispatch — lower handling variance for your protocol.'
            }, {
              icon: CheckCircle2,
              title: 'Matched batch docs',
              body: 'All COAs from the same dispatch window. Same testing lab, same release date.'
            }].map(({
              icon: Icon,
              title,
              body
            }) => <div key={title} className="flex items-start gap-3" data-id="element-1126">
                  <div className="h-9 w-9 rounded-xl bg-background border border-border flex items-center justify-center flex-shrink-0" data-id="element-1127">
                    <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} data-id="element-1128" />
                  </div>
                  <div className="min-w-0" data-id="element-1129">
                    <div className="text-[13.5px] font-semibold tracking-tight text-foreground leading-tight" data-id="element-1130">
                      {title}
                    </div>
                    <p className="mt-1.5 text-[12px] text-muted-foreground leading-relaxed" data-id="element-1131">
                      {body}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* GOAL FILTER PILLS */}
        <section className="w-full border-b border-border bg-card" data-id="element-1132">
          <div className="container mx-auto py-3.5" data-id="element-1133">
            <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-1134">
              {FILTER_GOALS.map(g => {
              const count = g === 'All' ? BUNDLES.length : BUNDLES.filter(b => b.primaryGoal === g).length;
              const isActive = activeGoal === g;
              const meta = g !== 'All' ? GOAL_META[g] : null;
              const Icon = meta?.icon;
              return <button key={g} onClick={() => setActiveGoal(g)} className={`inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium whitespace-nowrap transition-colors ${isActive ? 'bg-foreground text-background' : 'text-muted-foreground bg-background border border-border hover:text-foreground hover:border-foreground/30'}`} data-id="element-1135">
                    {Icon && <Icon className={`h-3 w-3 ${isActive ? '' : 'text-primary'}`} strokeWidth={2} data-id="element-1136" />}
                    {g === 'All' ? 'All stacks' : g}
                    <span className={`font-mono text-[11px] [font-feature-settings:'tnum'] ${isActive ? 'text-background/60' : 'text-muted-foreground/70'}`} data-id="element-1137">
                      {count}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </section>

        {/* Bundle list */}
        <section className="container mx-auto py-12 lg:py-16" data-id="element-1138">
          {filtered.length === 0 ? <div className="py-20 text-center border border-dashed border-border rounded-2xl" data-id="element-1139">
              <p className="text-muted-foreground text-sm" data-id="element-1140">
                No stacks in this category yet.
              </p>
              <button onClick={() => setActiveGoal('All')} className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground hover:text-primary transition-colors" data-id="element-1141">
                See all stacks
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} data-id="element-1142" />
              </button>
            </div> : <div className="space-y-8 lg:space-y-10" data-id="element-1143">
              {filtered.map(b => <BundleCard key={b.id} bundle={b} data-id="element-1144" />)}
            </div>}
        </section>

        {/* CTA strip */}
        <section className="w-full bg-foreground text-background" data-id="element-1145">
          <div className="container mx-auto py-16 lg:py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8" data-id="element-1146">
            <div className="max-w-xl" data-id="element-1147">
              <h2 className="text-3xl lg:text-4xl font-semibold tracking-[-0.02em] leading-tight" data-id="element-1148">
                Need a custom stack?
              </h2>
              <p className="mt-3 text-background/70 text-[15px] leading-relaxed" data-id="element-1149">
                Running a protocol with more than three peptides, or a recurring
                order schedule? Email our lab team directly — custom pricing for
                4+ peptide stacks.
              </p>
            </div>
            <a href="mailto:research@peptides.ml" className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-primary text-primary-foreground text-[14px] font-semibold tracking-tight transition-all hover:-translate-y-px" data-id="element-1150">
              research@peptides.ml
              <ChevronRight className="h-4 w-4" strokeWidth={2} data-id="element-1151" />
            </a>
          </div>
        </section>
      </main>

      <Footer data-id="element-1152" />
    </div>;
}